// Thumb is encoded in the image filename: `1-{w}-{h}-{base64urlhash}-{uid}.avif`
// The hash is the ThumbHash binary, base64url-encoded (same scheme as `make-thumbhash.ts`).
// Decoding is pure math (inverse DCT), produces a tiny ~32px PNG data URL — no network request.

function decodeBase64Url(s: string): Uint8Array {
  // Restore standard base64 alphabet + padding, then use atob.
  const b64 = s.replaceAll('-', '+').replaceAll('_', '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const binary = atob(padded)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.codePointAt(i) ?? 0
  return out
}

function thumbHashToRGBA(hash: ArrayLike<number>): {
  w: number
  h: number
  rgba: Uint8Array
} {
  const { PI, min, max, cos, round } = Math
  const header24 = hash[0] | (hash[1] << 8) | (hash[2] << 16)
  const header16 = hash[3] | (hash[4] << 8)
  const l_dc = (header24 & 63) / 63
  const p_dc = ((header24 >> 6) & 63) / 31.5 - 1
  const q_dc = ((header24 >> 12) & 63) / 31.5 - 1
  const l_scale = ((header24 >> 18) & 31) / 31
  const hasAlpha = header24 >> 23
  const p_scale = ((header16 >> 3) & 63) / 63
  const q_scale = ((header16 >> 9) & 63) / 63
  const isLandscape = header16 >> 15
  const lx = max(3, isLandscape ? (hasAlpha ? 5 : 7) : header16 & 7)
  const ly = max(3, isLandscape ? header16 & 7 : hasAlpha ? 5 : 7)
  const a_dc = hasAlpha ? (hash[5] & 15) / 15 : 1
  const a_scale = (hash[5] >> 4) / 15
  const ac_start = hasAlpha ? 6 : 5
  let ac_index = 0

  const decodeChannel = (nx: number, ny: number, scale: number) => {
    const ac: number[] = []
    for (let cy = 0; cy < ny; cy++)
      for (let cx = cy ? 0 : 1; cx * ny < nx * (ny - cy); cx++)
        ac.push(
          (((hash[ac_start + (ac_index >> 1)] >> ((ac_index++ & 1) << 2)) &
            15) /
            7.5 -
            1) *
            scale,
        )
    return ac
  }

  const l_ac = decodeChannel(lx, ly, l_scale)
  const p_ac = decodeChannel(3, 3, p_scale * 1.25)
  const q_ac = decodeChannel(3, 3, q_scale * 1.25)
  const a_ac = hasAlpha ? decodeChannel(5, 5, a_scale) : []

  const header = hash[3]
  const lx2 = isLandscape ? header & 7 : header & 7
  const ly2 = isLandscape ? header & 7 : hasAlpha ? 5 : 7
  const ratio =
    (isLandscape ? (hasAlpha ? 5 : 7) : lx2) /
    (isLandscape ? ly2 : hasAlpha ? 5 : 7)
  const w = round(ratio > 1 ? 32 : 32 * ratio)
  const h = round(ratio > 1 ? 32 / ratio : 32)
  const rgba = new Uint8Array(w * h * 4)
  const fx: number[] = []
  const fy: number[] = []

  for (let i = 0, y = 0; y < h; y++) {
    for (let x = 0; x < w; x++, i += 4) {
      let a = a_dc,
        l = l_dc,
        p = p_dc,
        q = q_dc
      for (let cx = 0, n = max(lx, hasAlpha ? 5 : 3); cx < n; cx++)
        fx[cx] = cos((PI / w) * (x + 0.5) * cx)
      for (let cy = 0, n = max(ly, hasAlpha ? 5 : 3); cy < n; cy++)
        fy[cy] = cos((PI / h) * (y + 0.5) * cy)
      for (let cy = 0, j = 0; cy < ly; cy++)
        for (
          let cx = cy ? 0 : 1, fy2 = fy[cy] * 2;
          cx * ly < lx * (ly - cy);
          cx++, j++
        )
          l += l_ac[j] * fx[cx] * fy2
      for (let cy = 0, j = 0; cy < 3; cy++)
        for (let cx = cy ? 0 : 1, fy2 = fy[cy] * 2; cx < 3 - cy; cx++, j++) {
          const f = fx[cx] * fy2
          p += p_ac[j] * f
          q += q_ac[j] * f
        }
      if (hasAlpha)
        for (let cy = 0, j = 0; cy < 5; cy++)
          for (let cx = cy ? 0 : 1, fy2 = fy[cy] * 2; cx < 5 - cy; cx++, j++)
            a += a_ac[j] * fx[cx] * fy2
      const b = l - (2 / 3) * p
      const r = (3 * l - b + q) / 2
      const g = r - q
      rgba[i] = max(0, 255 * min(1, r))
      rgba[i + 1] = max(0, 255 * min(1, g))
      rgba[i + 2] = max(0, 255 * min(1, b))
      rgba[i + 3] = max(0, 255 * min(1, a))
    }
  }
  return { w, h, rgba }
}

function rgbaToDataURL(w: number, h: number, rgba: ArrayLike<number>): string {
  const row = w * 4 + 1
  const idat = 6 + h * (5 + row)
  const bytes = [
    137,
    80,
    78,
    71,
    13,
    10,
    26,
    10,
    0,
    0,
    0,
    13,
    73,
    72,
    68,
    82,
    0,
    0,
    w >> 8,
    w & 255,
    0,
    0,
    h >> 8,
    h & 255,
    8,
    6,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    idat >>> 24,
    (idat >> 16) & 255,
    (idat >> 8) & 255,
    idat & 255,
    73,
    68,
    65,
    84,
    120,
    1,
  ]
  const table = [
    0, 498536548, 997073096, 651767980, 1994146192, 1802195444, 1303535960,
    1342533948, -306674912, -267414716, -690576408, -882789492, -1687895376,
    -2032938284, -1609899400, -1111625188,
  ]
  let a = 1,
    b = 0
  for (let end = row - 1, i = 0, y = 0; y < h; y++, end += row - 1) {
    bytes.push(
      y + 1 < h ? 0 : 1,
      row & 255,
      row >> 8,
      ~row & 255,
      (row >> 8) ^ 255,
      0,
    )
    for (b = (b + a) % 65521; i < end; i++) {
      const u = rgba[i] & 255
      bytes.push(u)
      a = (a + u) % 65521
      b = (b + a) % 65521
    }
  }
  bytes.push(
    b >> 8,
    b & 255,
    a >> 8,
    a & 255,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    73,
    69,
    78,
    68,
    174,
    66,
    96,
    130,
  )
  for (const [start, end] of [
    [12, 29],
    [37, 41 + idat],
  ] as const) {
    let c = ~0
    let e = end
    for (let i = start; i < e; i++) {
      c ^= bytes[i]
      c = (c >>> 4) ^ table[c & 15]
      c = (c >>> 4) ^ table[c & 15]
    }
    c = ~c
    bytes[e++] = c >>> 24
    bytes[e++] = (c >> 16) & 255
    bytes[e++] = (c >> 8) & 255
    bytes[e++] = c & 255
  }
  return `data:image/png;base64,${btoa(String.fromCodePoint(...bytes))}`
}

// Filename format: `{version}-{w}-{h}-{base62thumbhash}-{uid}.avif`
export function getThumb(
  src?: string,
): { w: number; ar: string; dataUrl: string } | undefined {
  if (!src) return undefined
  try {
    const fileName = src.split('/').pop()
    if (!fileName) return undefined
    const [, w, h, thumbHash] = fileName.split('-')
    if (!(w && h && thumbHash)) return undefined
    const nw = +w
    const nh = +h
    if (Number.isNaN(nw) || Number.isNaN(nh)) return undefined
    const hash = decodeBase64Url(thumbHash)
    const img = thumbHashToRGBA(hash)
    const dataUrl = rgbaToDataURL(img.w, img.h, img.rgba)
    return { w: nw, ar: `${nw} / ${nh}`, dataUrl }
  } catch {
    // swallow decode errors
  }
  return undefined
}
